import { of, merge, combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  debounceTime,
} from 'rxjs/operators';
import {
  MultiPointInstallationAddSensorSidebarAction,
  MultiPointInstallationAddSensorSidebarActionTypes,
  MultiPointInstallationAddSensorSidebarState,
} from '../../types/multi-point-installation-add-sensor-sidebar';
import { Capture, Organization } from '../../types/api';
import {
  RESOURCE_IDLE,
  RESOURCE_LOADING,
  ResourceStatus,
} from '../../types/resource';
import {
  serializeListViewState,
  updateQueryString,
} from '../../utils/rx-store-serialization';
import { getRequiredSessionToken } from '../../utils/session-token';
import { generateParams } from '../../rx-actions/load-in-configuration';

import createRxStore, { actions, ofType, rxDispatch } from '..';
import SessionStore from '../session';
import { SessionStatus } from '../../types/session';
import { SORT_EMPTY } from '../../types/sort';

import { SenseiApi } from '../../utils/api';

export const initialState: MultiPointInstallationAddSensorSidebarState = {
  selectedSensorSerialNumbers: [],
  filters: [],
  searchText: '',
  sort: SORT_EMPTY,

  page: 1,

  resource: RESOURCE_IDLE,
};

function reducer(
  state: MultiPointInstallationAddSensorSidebarState,
  action: MultiPointInstallationAddSensorSidebarAction,
): MultiPointInstallationAddSensorSidebarState {
  switch (action.type) {
    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_OPEN:
      return initialState;

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_LOAD_COMPLETE:
      return {
        ...state,
        selectedSensorSerialNumbers: state.selectedSensorSerialNumbers,

        page: 1,

        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            sensors: action.sensors,
            total: action.total,
            fetchingMoreSensors: RESOURCE_IDLE,
          },
        },
      };

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_LOAD_START:
      return {
        ...state,
        resource: RESOURCE_LOADING,
      };
    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_LOAD_ERROR:
      return {
        ...state,
        resource: { status: ResourceStatus.ERROR, error: action.error },
      };
  }

  if (state.resource.status !== ResourceStatus.COMPLETE) {
    return state;
  }

  switch (action.type) {
    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_SEARCH_TEXT:
      return {
        ...state,
        searchText: action.text,
      };

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_SORT:
      return {
        ...state,
        sort: { column: action.column, direction: action.direction },
      };

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_FILTERS:
      return {
        ...state,
        filters: action.filters,
        selectedSensorSerialNumbers: [],
      };

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_CHANGE_CHECKS:
      return {
        ...state,
        selectedSensorSerialNumbers: action.checked
          ? Array.from(
              new Set([
                ...state.selectedSensorSerialNumbers,
                ...action.serialNumbers,
              ]),
            )
          : state.selectedSensorSerialNumbers.filter(
              (sn) => !action.serialNumbers.includes(sn),
            ),
      };

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_PAGE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreSensors: RESOURCE_LOADING,
          },
        },
      };

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_NEXT_PAGE_LOAD_ERROR:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            fetchingMoreSensors: {
              status: ResourceStatus.ERROR,
              error: action.error,
            },
          },
        },
      };

    case MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_NEXT_PAGE_LOAD_COMPLETE:
      return {
        ...state,
        resource: {
          status: ResourceStatus.COMPLETE,
          data: {
            ...state.resource.data,
            sensors: [...state.resource.data.sensors, ...action.sensors],
            fetchingMoreSensors: RESOURCE_IDLE,
          },
        },
      };

    default:
      return state;
  }
}

const MultiPointInstallationAddSensorSidebarStore = createRxStore(
  'MultiPointInstallationAddSensorSidebarStore',
  initialState,
  reducer,
);

// =================================
// SIDE EFFECT: Load first page of installation data when one of these occurs:
// 1. Page is initially loaded (possibly with a new organization, so extract the organization from the action)
// 3. Search text is updated (has a debounce in it)
// 3. Filters or sort are updated
// =================================
const initialLoadStream = actions.pipe(
  ofType<{ organizationId: Organization['id'] | null }>(
    MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_OPEN,
  ),
  switchMap(
    (action) =>
      combineLatest(
        MultiPointInstallationAddSensorSidebarStore.pipe(take(1)),
        SessionStore.pipe(take(1)),
      ),
    (action, [inConfigurationState, sessionState]) => ({
      inConfigurationState,
      sessionState,
      organizationId: action.organizationId,
    }),
  ),
);

const searchTextUpdateStream = actions.pipe(
  ofType(
    MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_SEARCH_TEXT,
  ),
  debounceTime(1000),
  switchMap(
    () =>
      combineLatest(
        MultiPointInstallationAddSensorSidebarStore.pipe(take(1)),
        SessionStore.pipe(take(1)),
      ),
    (action, [inConfigurationState, sessionState]) => ({
      inConfigurationState,
      sessionState,
      organizationId:
        sessionState.status === SessionStatus.LOGGED_IN
          ? sessionState.selectedOrganizationId
          : null,
    }),
  ),
);

const sortFilterUpdateStream = actions.pipe(
  ofType(
    MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_FILTERS,
    MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_SORT,
  ),
  switchMap(
    () =>
      combineLatest(
        MultiPointInstallationAddSensorSidebarStore.pipe(take(1)),
        SessionStore.pipe(take(1)),
      ),
    (action, [inConfigurationState, sessionState]) => ({
      inConfigurationState,
      sessionState,
      organizationId:
        sessionState.status === SessionStatus.LOGGED_IN
          ? sessionState.selectedOrganizationId
          : null,
    }),
  ),
);

merge(
  initialLoadStream,
  searchTextUpdateStream,
  sortFilterUpdateStream,
).subscribe(({ inConfigurationState, sessionState, organizationId }) => {
  if (sessionState.status !== SessionStatus.LOGGED_IN) {
    return;
  }

  rxDispatch({
    type: MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_LOAD_START,
  });

  SenseiApi.sensorList(
    sessionState.token,
    generateParams(
      inConfigurationState.filters,
      inConfigurationState.searchText,
      inConfigurationState.sort,
      organizationId,
      {},
    ),
  )
    .then((response) => {
      rxDispatch({
        type: MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_LOAD_COMPLETE,
        sensors: response.results,
        total: response.total,
      });
    })
    .catch((error) => {
      rxDispatch({
        type: MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_LOAD_ERROR,
        error,
      });
    });
});

// =================================
// SIDE EFFECT: when a user reaches the end of the sidebar list, load more data
// =================================
actions
  .pipe(
    ofType(
      MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_SET_PAGE,
    ),
    switchMap(
      () =>
        combineLatest(
          MultiPointInstallationAddSensorSidebarStore.pipe(take(1)),
          SessionStore.pipe(take(1)),
        ),
      (action, [sidebarState, sessionState]) => ({
        action,
        sidebarState,
        sessionState,
      }),
    ),
  )
  .subscribe(async ({ action, sidebarState, sessionState }) => {
    if (sessionState.status !== SessionStatus.LOGGED_IN) {
      return;
    }

    const a = action as any;

    rxDispatch({
      type: MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_NEXT_PAGE_LOAD_LOADING,
    });

    // Apply filters to the results that are returned.
    const params = {
      ...generateParams(
        sidebarState.filters,
        sidebarState.searchText,
        sidebarState.sort,
        sessionState.selectedOrganizationId,
        {},
      ),
      page: a.value,
    };

    let sensors: any;
    try {
      sensors = await SenseiApi.sensorList(sessionState.token, params);
    } catch (error) {
      rxDispatch({
        type: MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_NEXT_PAGE_LOAD_ERROR,
        error,
      });
      return;
    }

    rxDispatch({
      type: MultiPointInstallationAddSensorSidebarActionTypes.MULTI_POINT_INSTALLATION_ADD_SENSOR_SIDEBAR_NEXT_PAGE_LOAD_COMPLETE,
      sensors: sensors.results,
    });
  });

export default MultiPointInstallationAddSensorSidebarStore;
